import React, { useState } from "react";
import BottomSheet from "../BottomSheet";
import { AlertTriangle } from "lucide-react";
import Input from "../Input";
import Button from "../Button";
import { useNavigate, useParams } from "react-router-dom";
import Radio from "src/components/common/Radio";
import {
  pincodeValidationRequest,
  updateAddressRequest,
} from "src/redux/slices/locationSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { PincodeValidationResponse } from "src/types/location.types";
import Alert from "../Alert";

interface AddressFormBottomSheetProps {
  showBottomSheet: boolean;
  setShowBottomSheet: (show: boolean) => void;
  pincode: string;
  houseNoAndBuilding: string;
  street: string;
  lat: number;
  lng: number;
  setHouseNoAndBuilding: (houseNoAndBuilding: string) => void;
  isLocationToEdit: boolean;
  addressId: string;
  addressType: "Home" | "Work" | "Other";
}

const addressTypeOptions: {
  value: "Home" | "Work" | "Other";
  label: string;
}[] = [
  { value: "Home", label: "Home" },
  { value: "Work", label: "Work" },
  { value: "Other", label: "Other" },
];

const AddressFormBottomSheet: React.FC<AddressFormBottomSheetProps> =
  React.memo(
    ({
      showBottomSheet,
      setShowBottomSheet,
      pincode,
      houseNoAndBuilding,
      street,
      setHouseNoAndBuilding,
      lat,
      lng,
      isLocationToEdit,
      addressId,
      addressType,
    }: AddressFormBottomSheetProps) => {
      const params = useParams();
      const supportProgramKey = params["support-program-key"];
      const navigate = useNavigate();
      const [streetName, setStreetName] = useState<string>(street);
      const [addressTypeState, setAddressTypeState] = useState<
        "Home" | "Work" | "Other"
      >(addressType);
      const dispatch = useAppDispatch();
      const patientDetails = useAppSelector((state) => state.auth.patient.data);
      const [isSavingAddress, setIsSavingAddress] = useState(false);

      const addAddress = () => {
        setIsSavingAddress(true);
        const payload = {
          name: patientDetails?.name,
          pincode: Number(pincode),
          contact_no: patientDetails?.contact_no,
          address_type: addressTypeState,
          address: houseNoAndBuilding,
          street: streetName,
          latitude: lat,
          longitude: lng,
        };
        if (addressId) {
          Object?.assign(payload, {
            address_id: addressId,
          });
        }
        if (patientDetails?.active_plan_details?.patient_plan_rel_id) {
          Object?.assign(payload, {
            patient_plan_rel_id:
              patientDetails?.active_plan_details?.patient_plan_rel_id,
          });
        }
        new Promise((resolve, reject) => {
          dispatch(
            updateAddressRequest({
              resolve,
              reject,
              payload,
            })
          );
        })
          .then((res) => {
            navigate(`/app/${supportProgramKey}/book-lab-test/checkout`, {
              state: { isRefetchCartDetails: true },
            });
          })
          .catch((err) => {
            console.error("Error adding/updating address", err);
          })
          .finally(() => {
            setIsSavingAddress(false);
          });
      };

      const handleSaveAddress = () => {
        const payload = { pincode: pincode };
        new Promise<PincodeValidationResponse["data"]>((resolve, reject) => {
          dispatch(
            pincodeValidationRequest({
              payload,
              resolve,
              reject,
            })
          );
        })
          .then((res) => {
            if (res?.status === "OK") {
              addAddress();
            } else {
              console.error("Pincode validation error", res);
            }
          })
          .catch((err) => {
            console.error("Error validating pincode", err);
          });
      };

      return (
        <BottomSheet
          title="Enter Address"
          show={showBottomSheet}
          handleClose={() => setShowBottomSheet(false)}
          showCloseButton
        >
          {/* Address content goes here */}
          <div className="relative w-full p-4 space-y-4">
            {/* Address Input Form */}
            <Alert
              title="Disclaimer"
              description="We need this information to deliver your product & for collecting test samples."
              variant="warning"
              icon={<AlertTriangle />}
            />

            <div>
              <Input
                name="pincode"
                type="text"
                placeholder="Enter Pincode *"
                fullWidth
                onChange={(e) => {
                  //setPincode(e.target.value);
                }}
                value={pincode}
                disabled={pincode ? true : false}
              />
            </div>
            <div>
              <Input
                name="houseNoAndBuilding"
                type="text"
                placeholder="House Number and Building *"
                fullWidth
                onChange={(e) => {
                  setHouseNoAndBuilding(e.target.value);
                }}
                value={houseNoAndBuilding}
              />
            </div>
            <div>
              <Input
                name="streetName"
                type="text"
                placeholder="Street Name *"
                fullWidth
                onChange={(e) => {
                  setStreetName(e.target.value);
                }}
                value={streetName}
              />
            </div>
            <div>
              <h2 className="text-sm font-medium mb-2">Address Type</h2>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  {addressTypeOptions?.map((opt) => (
                    <div key={opt?.value}>
                      <div
                        className="flex items-center gap-2"
                        onClick={() => setAddressTypeState(opt?.value)}
                      >
                        <Radio
                          checked={addressTypeState === opt?.value}
                          onChange={() => setAddressTypeState(opt?.value)}
                        />
                        <p className="text-sm text-[var(--text-primary)]">
                          {opt?.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Save Address Button */}
            <div>
              <Button
                rounded="full"
                fullWidth
                disabled={
                  !pincode || !houseNoAndBuilding || !streetName || !addressType
                }
                onClick={handleSaveAddress}
                loading={isSavingAddress}
              >
                Save Address
              </Button>
            </div>
          </div>
        </BottomSheet>
      );
    }
  );

export default AddressFormBottomSheet;
